/**
 * <product-tabs>
 *
 * Tabs on desktop, an accordion on mobile — from one set of authored panels.
 *
 * Author one <article data-tab-title="..."> per panel and nothing else. The
 * component generates the desktop <nav> and the mobile collapse chrome around
 * that content, so a section only ever writes the panel bodies. This is what
 * lets sections with bespoke tab content (metafields, PDF lists) share the same
 * markup as the theme-editor-driven v2-product-tabs snippet — Liquid has no way
 * to pass a block of markup into a snippet, but the DOM does.
 *
 *   <product-tabs class="main-product__tabs">
 *     <article data-tab-title="Delivery &amp; Returns">…</article>
 *     <article data-tab-title="Tearsheet/Guides">…</article>
 *   </product-tabs>
 *
 * Visibility is CSS's job, keyed off [data-active] and scoped to
 * [data-initialised] so the panels stay readable before (or without) JS.
 * See v2-product-tabs.css.
 */

const CHEVRON = `
  <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M1 1L5 5L9 1" stroke="currentColor"/>
  </svg>
`;

let instanceCount = 0;

class ProductTabs extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialised) return;

    this.panels = Array.from(this.querySelectorAll(':scope > article[data-tab-title]'));
    if (!this.panels.length) return;

    instanceCount += 1;
    this.uid = `product-tabs-${instanceCount}`;

    this.build();
    this.selectTab(0);

    this.dataset.initialised = 'true';
  }

  build() {
    const nav = document.createElement('nav');
    const content = document.createElement('div');
    content.className = 'main-product__tabs__content';

    this.buttons = this.panels.map((panel, index) => {
      const title = panel.dataset.tabTitle;
      const panelId = `${this.uid}-panel-${index}`;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'copy-medium';
      button.textContent = title;
      button.addEventListener('click', () => this.selectTab(index));
      nav.appendChild(button);

      // Move the authored content into the collapsing box, then sit the
      // checkbox and label in front of it — the CSS collapse relies on that
      // sibling order.
      const inner = document.createElement('div');
      inner.className = 'main-product__tabs__content--inner copy-medium';
      inner.append(...panel.childNodes);

      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'main-product__tabs__collapse-checkbox';
      checkbox.id = panelId;
      // Checked means collapsed, so only the first panel starts open.
      checkbox.checked = index !== 0;

      const label = document.createElement('label');
      label.className = 'main-product__tabs__collapse-title copy-light';
      label.setAttribute('for', panelId);
      label.append(title);
      label.insertAdjacentHTML('beforeend', CHEVRON);

      panel.append(checkbox, label, inner);
      content.appendChild(panel);

      return button;
    });

    this.replaceChildren(nav, content);
  }

  selectTab(index) {
    this.panels.forEach((panel, i) => panel.toggleAttribute('data-active', i === index));
    this.buttons.forEach((button, i) => {
      button.classList.toggle('main-product__tabs--active', i === index);
    });
  }
}

customElements.define('product-tabs', ProductTabs);
